import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, NgModule, OnInit, Output } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBars, faLightbulb, faLightbulbOn } from '@fortawesome/pro-light-svg-icons';
import { faQuestion, faUser } from '@fortawesome/pro-solid-svg-icons';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { map } from 'rxjs/operators';
import { CarrierModule } from '../carrier/carrier';
import { UserModule } from '../services/user';
import { StationSearch, StationSearchModule } from '../station-search/station-search';
import { LinkComponent } from './link/link.component';
import { ShowNavPipe } from './pipes/show-nav.pipe';
import { UserGroupsPipe } from './pipes/user-groups.pipe';
import { SideNavComponent } from './side-nav/side-nav.component';
import { TopNavComponent } from './top-nav/top-nav.component';
import { UserNavComponent } from './user-nav/user-nav.component';

export interface SubNavLinks {
  path: string;
  title: string;
  badge?: NavBadge;
  externalLink?: boolean;
}

export interface NavBadge {
  Count: number;
  Class?: 'primary' | 'accent' | 'warn';
}

export interface NavLinks {
  show: boolean;
  title: string;
  path?: string;
  subNav?: SubNavLinks[];
  active?: boolean;
  badge?: NavBadge;
  openSubNav?: boolean;
  externalLink?: boolean;
}

export interface Role {
  title: string;
  group: string;
}

export interface SubNavLinksWithIcon extends SubNavLinks {
  icon: string;
}

export interface NavLinksWithIcon extends NavLinks {
  icon: string;
  subNav?: SubNavLinksWithIcon[];
}

@Component({
  selector: 'to-navigation',
  templateUrl: './navigation.html',
  styleUrls: ['./navigation.scss']
})
export class NavigationComponent implements OnInit {
  private static readonly _LOCAL_STORAGE_KEY = 'SHAREPOINT_TECHOPS_DARK_MODE';

  @Input() navBar: NavLinksWithIcon[];
  @Input() brand = '';
  @Input() prod = window.location.pathname.indexOf('TechOpsAppsStage') === -1;
  @Input() showUser = true;
  @Input() showUserRoles: Role[];
  @Input() stationSearch = false;
  @Input() showRegions = false;
  @Input() helpLink: string;
  @Input() requireMatch = true;

  @Output() stationChanged = new EventEmitter<StationSearch>();

  isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(map(result => result.matches));
  dark = JSON.parse(localStorage[NavigationComponent._LOCAL_STORAGE_KEY] || 'false');

  constructor(
    private breakpointObserver: BreakpointObserver,
  ) { }

  ngOnInit() {
    this._toggleDarkModeClass();
  }

  toggleDarkMode() {
    localStorage[NavigationComponent._LOCAL_STORAGE_KEY] = this.dark = !this.dark;
    this._toggleDarkModeClass();
  }

  private _toggleDarkModeClass() {
    window.document.body.classList[this.dark ? 'add' : 'remove']('dark-theme');
  }

}

@NgModule({
  imports: [
    // core
    CommonModule,
    RouterModule,
    // icons
    FontAwesomeModule,
    // loading bar
    LoadingBarHttpClientModule,
    LoadingBarRouterModule,
    // materials
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatDividerModule,
    MatMenuModule,
    MatTabsModule,
    MatButtonModule,
    MatBadgeModule,
    // ng partners
    UserModule,
    CarrierModule,
    StationSearchModule,
  ],
  declarations: [
    UserGroupsPipe,
    ShowNavPipe,
    NavigationComponent,
    UserNavComponent,
    TopNavComponent,
    SideNavComponent,
    LinkComponent,
  ],
  exports: [NavigationComponent],
})
export class NavigationModule {
  constructor(
    library: FaIconLibrary,
  ) {
    library.addIcons(faLightbulb, faLightbulbOn, faBars, faQuestion, faUser);
  }
}
